import { create } from "zustand";
import { getList, ListItem } from "../apis/dashboard/list";
import { SummaryData, getSummary } from "../apis/dashboard/summary";

interface ListStore {
  list: ListItem[];
  summary: SummaryData;
  page: {
    size: number;
    hasNext: boolean;
    hasPrevious: boolean;
    totalPages: number;
    totalItems: number;
    currentPage: number;
  };
  fetchList: (params: { page: number; size: number }) => Promise<void>;
  fetchSummary: () => Promise<void>;
}

export const useListStore = create<ListStore>((set) => ({
  list: [],
  summary: {
    totalCount: 0,
    totalDuration: 0,
    video: 0,
    audio: 0,
  },
  page: {
    size: 10,
    hasNext: false,
    hasPrevious: false,
    totalPages: 1,
    totalItems: 0,
    currentPage: 1,
  },
  fetchList: async (params = { page: 0, size: 10 }) => {
    try {
      const { page = 0, size = 10 } = params;
      const response = await getList({
        page,
        size,
        status: ["completed", "failed", "running"],
      });
  
      if (response && response.content) {
        const content = response.content;
        const list = Array.isArray(content.list) ? content.list : [];
        const pageInfo = content.page ?? {};
  
        const totalPages = pageInfo.totalPages || 1;
        const totalItems = pageInfo.totalItems || 0;
        const sizePerPage = pageInfo.size || size;
        const currentPageZeroBased = page ?? 0;
  
        set((state) => ({
          ...state,
          list: list,
          page: {
            size: sizePerPage,
            hasNext: pageInfo.hasNext ?? (currentPageZeroBased + 1 < totalPages),
            hasPrevious:
              pageInfo.hasPrevious ?? (currentPageZeroBased > 0),
            totalPages,
            totalItems,
            currentPage: currentPageZeroBased + 1,
          },
        }));
      } else {
        console.warn("Invalid response structure for list:", response);
        set((state) => ({
          ...state,
          list: [],
          page: {
            ...state.page,
            hasNext: false,
            hasPrevious: false,
            totalPages: 1,
            totalItems: 0,
            currentPage: 1,
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch list:", error);
      set((state) => ({
        ...state,
        list: [],
        page: { ...state.page, hasNext: false, hasPrevious: false },
      }));
    }
  },  
  fetchSummary: async () => {
    try {
      const response = await getSummary();
      
      // 안전한 접근
      if (response && response.content) {
        const summaryData = {
          totalCount: response.content.totalCount || 0,
          totalDuration: response.content.totalDuration || 0,
          video: response.content.video || 0,
          audio: response.content.audio || 0,
        };
        set({ summary: summaryData });
      } else {
        console.warn("Invalid response structure for summary:", response);
        set({
          summary: {
            totalCount: 0,
            totalDuration: 0,
            video: 0,
            audio: 0,
          },
        });
      }
    } catch (error) {
      console.error("Failed to fetch volume data:", error);
      set({
        summary: {
          totalCount: 0,
          totalDuration: 0,
          video: 0,
          audio: 0,
        },
      });
    }
  },
}));
